import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { DataList, DataRow, Section } from '@/components/ui/Page'
import { ErrorState, Loading } from '@/components/ui/States'
import { supabase, describeError } from '@/lib/supabase'
import { formatInstant } from '@/lib/dates'
import { useCurrentStaff } from '@/features/auth/RequireStaff'
import { MfaSetup } from '@/features/auth/MfaSetup'
import { PASSWORD_SET_FLAG } from '@/features/auth/SetPasswordPrompt'

/**
 * The signed-in person's own account: password, second factor, session.
 *
 * Separate from business settings because the permissions differ — the
 * assistant cannot change the VAT rate but must be able to change their own
 * password — and because mixing "who we are as a business" with "who I am" in
 * one form makes both harder to scan.
 */
export function AccountSettingsPage() {
  const { staff } = useCurrentStaff()
  const queryClient = useQueryClient()

  const factorsQuery = useQuery({
    queryKey: ['mfa-factors'],
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors()
      if (error) throw error
      return data
    },
  })

  const [email, setEmail] = useState<string | null>(null)

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setEmail(data.user?.email ?? null))
  }, [])

  async function setMfaRequired(next: boolean) {
    const { error } = await supabase.from('staff').update({ mfa_required: next }).eq('id', staff.id)

    if (error) {
      toast.error('Could not change that', { description: describeError(error) })
      return
    }

    await queryClient.invalidateQueries({ queryKey: ['staff'] })
    toast.success(next ? 'Two-step sign-in enforced' : 'Two-step sign-in made optional')
  }

  const enrolled = (factorsQuery.data?.totp ?? []).some((factor) => factor.status === 'verified')

  return (
    <div className="pb-16">
      <Section title="You">
        <DataList className="max-w-[30rem]">
          <DataRow label="Name">{staff.full_name}</DataRow>
          <DataRow label="Email">{email ?? '—'}</DataRow>
          <DataRow label="Role">{staff.role === 'owner' ? 'Owner' : 'Assistant'}</DataRow>
          <DataRow label="Added">{formatInstant(staff.created_at)}</DataRow>
        </DataList>
      </Section>

      <Section
        title="Two-step sign-in"
        description="A code from an authenticator app, on top of your password. Worth the thirty seconds it takes to set up — this database holds your clients' personal details."
      >
        {factorsQuery.isPending ? (
          <Loading label="Checking" />
        ) : factorsQuery.isError ? (
          <ErrorState
            title="Could not check your authenticator"
            detail={describeError(factorsQuery.error)}
            onRetry={() => void factorsQuery.refetch()}
          />
        ) : (
          <MfaSetup
            enrolled={enrolled}
            required={staff.mfa_required}
            onRequiredChange={(next) => void setMfaRequired(next)}
          />
        )}
      </Section>

      <Section title="Password" description="At least twelve characters, with an uppercase letter and a digit.">
        <ChangePassword />
      </Section>
    </div>
  )
}

function ChangePassword() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mismatch = confirm.length > 0 && password !== confirm

  async function submit(event: React.FormEvent) {
    event.preventDefault()

    if (password !== confirm) {
      setError('The two passwords do not match.')
      return
    }

    setBusy(true)
    setError(null)

    // The same flag SetPasswordPrompt reads. Without it here, changing a
    // password in settings would leave the "choose a password" screen appearing
    // at every sign-in.
    const { error: updateError } = await supabase.auth.updateUser({
      password,
      data: { [PASSWORD_SET_FLAG]: true },
    })

    setBusy(false)

    if (updateError) {
      setError(describeError(updateError))
      return
    }

    setPassword('')
    setConfirm('')
    toast.success('Password changed')
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="flex max-w-[24rem] flex-col gap-5">
      {error ? <ErrorState title="Could not change your password" detail={error} /> : null}

      <Field label="New password">
        <Input
          type="password"
          autoComplete="new-password"
          minLength={12}
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>

      <Field label="Confirm it" error={mismatch ? 'The two passwords do not match.' : undefined}>
        <Input
          type="password"
          autoComplete="new-password"
          minLength={12}
          required
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />
      </Field>

      <Button
        type="submit"
        variant="primary"
        className="self-start"
        loading={busy}
        loadingLabel="Changing"
        disabled={password.length < 12 || mismatch}
      >
        Change password
      </Button>
    </form>
  )
}
